import { Pencil, SearchIcon, X } from "lucide-react";
import {
  type PropsWithChildren,
  createContext,
  memo,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TransactionsPanelHeader } from "@/features/transactions/shared/transactions-panel-header";

import { BankBookings } from "../../shared/bank-bookings";
import { InvoiceMatchingForm } from "../../shared/invoice-matching-form";

import { useSearchTransactionInvoices } from "@/queries/transactions/search-invoices";
import { useQueryState } from "nuqs";
import { BookingsReasonConfirmationFooter } from "./bookings-reason-confirmation-footer";

export enum BookingPanelTabs {
  MATCH = "match",
  BOOKING = "booking",
}

type Props = {
  transactions: Transaction[];
  onRowSelectionChange: (value: Record<string, boolean>) => void;
  rowSelection: Record<string, boolean>;
  onMatch: (invoiceIds: string[]) => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  onConfirmRawBookings?: (bankTxnIds?: string[]) => Promise<void>;
  onConfirmBookings?: (bankTxnIds?: string[]) => Promise<void>;
  view?: "documented" | "search";
  onClose: () => void;
};

const _BookingPanel = ({
  transactions,
  onRowSelectionChange,
  rowSelection,
  onMatch,
  onUnmatch,
  onConfirmBookings,
  onConfirmRawBookings,
  view,
  onClose,
}: Props) => {
  const [invoicesSearch, setInvoicesSearch] = useState("");

  const { t } = useTranslation();
  const { bookingPanelTab, setBookingPanelTab } = useBookingPanelContext();

  const transactionIds = useMemo(
    () => transactions.map((transaction) => transaction.bankTxnId),
    [transactions],
  );

  const {
    data: invoices,
    loading: invoicesLoading,
    error: invoicesError,
  } = useSearchTransactionInvoices({
    transactions,
    page: 1,
    limit: 50,
    offset: 0,
    global: invoicesSearch,
  });

  if (transactions.length === 0) {
    return null;
  }

  return (
    <div className="bg-background z-10 h-full">
      <div className="h-full rounded-md border relative">
        <div className="flex flex-col h-full">
          <div className="flex-1 flex flex-col gap-2 pt-4 h-full">
            <div className="px-4 flex items-start gap-2">
              <div className="flex-1">
                <TransactionsPanelHeader transactions={transactions} />
              </div>
              <button
                onClick={handleClose}
                className="p-1.5 rounded-md hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close panel"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <Tabs
              className="flex-1 flex flex-col"
              value={bookingPanelTab as BookingPanelTabs}
              onValueChange={(value) =>
                setBookingPanelTab(value as BookingPanelTabs)
              }
              defaultValue={bookingPanelTab}
            >
              <TabsList className="px-4">
                <TabsTrigger value={BookingPanelTabs.MATCH} className="gap-2">
                  <SearchIcon className="w-4 h-4" />
                  {t("transactions.accounting.panel.tabs.match.title")}
                </TabsTrigger>
                <TabsTrigger value={BookingPanelTabs.BOOKING} className="gap-2">
                  <Pencil className="w-4 h-4" />
                  {t("transactions.accounting.panel.tabs.booking.title")}
                </TabsTrigger>
              </TabsList>
              <TabsContent value={BookingPanelTabs.MATCH} className="flex-1">
                <InvoiceMatchingForm
                  transactionIds={transactionIds}
                  onRowSelectionChange={onRowSelectionChange}
                  rowSelection={rowSelection}
                  invoices={invoices || []}
                  loading={invoicesLoading}
                  error={invoicesError}
                  onMatch={handleMatch}
                  onUnmatch={onUnmatch}
                  rowPinningOnMatch={false}
                  onInvoicesSearchChange={setInvoicesSearch}
                  invoicesSearch={invoicesSearch}
                  className="px-4"
                  view={view}
                >
                  {({ transactions }) => {
                    if (!onConfirmBookings) {
                      return null;
                    }

                    return (
                      <div className="flex border-t px-4 py-4 items-center shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.06)]">
                        {/* // TODO: Get currency from transaction */}
                        <BookingsReasonConfirmationFooter
                          key={transactions
                            .map((transaction) => transaction.bankTxnId)
                            .join("")}
                          transactions={transactions}
                          currency="EUR"
                          toBookingsTab={() =>
                            setBookingPanelTab(BookingPanelTabs.BOOKING)
                          }
                          onConfirmBookings={onConfirmBookings}
                        />
                      </div>
                    );
                  }}
                </InvoiceMatchingForm>
              </TabsContent>
              <TabsContent
                value={BookingPanelTabs.BOOKING}
                className="flex-1 min-h-0"
              >
                <BankBookings
                  transactions={transactions}
                  onConfirmBookings={onConfirmRawBookings}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );

  async function handleMatch(invoiceIds: string[]) {
    await onMatch(invoiceIds);
  }

  function handleClose() {
    onClose();
  }
};

export const BookingPanel = memo(_BookingPanel);

const BookingPanelContext = createContext<{
  bookingPanelTab: BookingPanelTabs;
  setBookingPanelTab: (value: BookingPanelTabs) => void;
}>({
  bookingPanelTab: BookingPanelTabs.MATCH,
  setBookingPanelTab: () => {},
});

export const BookingPanelProvider = ({
  children,
  selectedTransactionIds,
}: PropsWithChildren<{ selectedTransactionIds: Record<string, boolean> }>) => {
  const [bookingPanelTab, setBookingPanelTab] = useQueryState(
    "bookingPanelTab",
    {
      defaultValue: BookingPanelTabs.MATCH,
      history: "replace",
    },
  );

  return (
    <BookingPanelContext.Provider
      value={{
        bookingPanelTab: bookingPanelTab as BookingPanelTabs,
        setBookingPanelTab,
      }}
    >
      {children}
    </BookingPanelContext.Provider>
  );
};

export const useBookingPanelContext = () => {
  const context = useContext(BookingPanelContext);
  if (!context) {
    throw new Error(
      "useBookingPanelContext must be used within a BookingPanelProvider",
    );
  }
  return context;
};
